import { defineStore } from 'pinia';
import type { CartType } from '~/constants/types/cart';
import CartData from '~/models/cart-data';
import { useConfigStore } from '~/store/config';
import useApiFetch from '~/composables/useApiFetch';
import type Product from '~/models/product';
import { pushToDataLayer } from '~/composables/useTracking';
import type CartItem from '~/models/cart-item';

export const useCartStore = defineStore(
  'cartStore', {
    state: () => ({
      _cartData: {} as CartType,
      showAddedItem: false,
      addedItem: null as { product: Product, itemId: string, quantity: number } | null,
      showCartMenu: false,
      showAccountMenu: false,
      accountMenuMessage: '' as string,
      highlightWishlist: false,
      cartIsUpdating: false,
      cartInitialized: false,
      hasRoundUpProductAvailable: null as boolean | null, // null = not checked yet, true/false = availability
    }),
    actions: {
      async initialCart() {
        const config = useConfigStore();
        const configMarket = config.market || {};
        const { country, language, market, pricelist } = configMarket;
        const { centraPut, handleApiError } = useApiFetch();

        const res = await centraPut<CartType>('/selection', {
          country,
          language,
        });
        if (res) {
          //console.log('the selection in initial: ' + JSON.stringify(res));
          this._cartData = res;
          this.cartInitialized = true;
          if (res.selection.centraCheckoutScript != '') {
            const s = document.createElement('script');
            s.type = 'text/javascript';
            const code = res.selection.centraCheckoutScript;
            try {
              s.appendChild(document.createTextNode(code));
              document.body.appendChild(s);
            } catch (e) {
              s.text = code;
              document.body.appendChild(s);
            }
            //console.log('Added script');
            //getCentraCheckoutScript(res.selection.centraCheckoutScript);
            //console.log('checkoutScript: ' + res.selection.centraCheckoutScript);
            //document.addEventListener('centra_checkout_callback', function(origdata) {
            //  console.log('centra_checkout_callback! '+origdata);
            //  var url = '{your-endpoint-which-does-PUT-checkout/payment-fields}'
            //  $.ajax(url, {
            //    method:"POST",
            //    data: origdata.detail,
            //    success: function(data) {
            //    // Ajax update data from response
            //      CentraCheckout.resume(origdata.detail.additionalFields.suspendIgnore);
            //    },
            //  });
            //});
            //centra_checkout_callback('test');
          }
        } else {
          handleApiError();
        }
      },
      async fetchCart() {
        const { centraGet, handleApiError } = useApiFetch();
        const res = await centraGet<CartType>('/selection');
        if (res) {
          console.log('[CartStore] Full selection response from Centra:', JSON.stringify(res, null, 2));
          console.log('[CartStore] Cart items detail:', JSON.stringify(res.selection?.items, null, 2));
          this._cartData = res;
        } else {
          handleApiError();
        }
      },

      async addToCart(payload: { product: Product, itemId: string, quantity: number }) {
        const { centraPost } = useApiFetch();
        const res = await centraPost<CartType>(
          `/items/${payload.itemId}/quantity/${payload.quantity}`
        );
        if (res) {
          this._cartData = res;
          this.addedItem = {
            product: payload.product,
            itemId: payload.itemId,
            quantity: payload.quantity,
          };
          this.showAddedItem = true;

          // Tracking
          const trackingPayload = {
            event: 'productAddToCart',
            ecommerce: {
              currencyCode: this.cartData.currency,
              add: {
                products: [
                  {
                    id: payload.product.sku,
                    feedId: payload.product.sku,
                    ...payload.product.gtmProductValues,
                    quantity: payload.quantity,
                  },
                ],
              },
            },
          };
          pushToDataLayer(trackingPayload);

        } else {
          /**
           * @todo - error hanling
           */
        }
      },

      async addGiftCardToCart(giftCertificate: string, item) {
        const { centraPost, handleApiError } = useApiFetch();
        const res = await centraPost<CartType>(
          `/items/gift-certificates/${giftCertificate}`
        );
        if (res) {
          this._cartData = res;
          
          // Transform gift certificate data to match product structure
          const amount = item?.amountByCurrency?.find(a => a.currency === this.cartData.currency) || item?.amountByCurrency?.[0];
          const giftCardProduct = {
            title: item?.name || 'Gift Card',
            price: amount?.amount || '',
            standardImage: '', // Gift cards typically don't have images in the fallout
            showAsOnSale: false,
            isGiftCard: true, // Flag to identify gift cards
          };
          
          // Set addedItem to show in cart fallout menu
          this.addedItem = {
            product: giftCardProduct as any,
            itemId: giftCertificate,
            quantity: 1,
          };
          this.showAddedItem = true;
          
          return true;
        } else {
          console.error();
          handleApiError();
          return false;
        }
      },

      async modifyItemQuantity({ line, quantity, item }:{ line: string, quantity: number, item: CartItem }) {

        this.cartIsUpdating = true;
        const originalQuantity = item.quantity;
        const diff = Math.abs(item.quantity - quantity);
        const { centraPut } = useApiFetch();
        const res = await centraPut<CartType>(`/lines/${line}/quantity/${quantity}`);
        if (res) {
          this._cartData = res;

          if (quantity !== originalQuantity) {
            const payload = {
              event: quantity < originalQuantity ? 'productRemoveFromCart':'productAddToCart',
              ecommerce: {
                currencyCode: this.cartData.currency,
                remove: {
                  products: [
                    {
                      id: item.sku,
                      feedId: item.sku,
                      ...item.product.gtmProductValues,
                      quantity: diff,
                    },
                  ],
                },
              },
            };
            pushToDataLayer(payload);
          }

        }
        this.cartIsUpdating = false;
      },
      async deleteFromCart(payload: { line: string, item?: CartItem }) {
        this.cartIsUpdating = true;
        const { centraDelete } = useApiFetch();
        console.log('Deleting line: ' + payload.line);
        const res = await centraDelete<CartType>(`/lines/${payload.line}`);
        if (res) {
          this._cartData = res;
          if (payload.item) {
            const trackingPayload = {
              event: 'productRemoveFromCart',
              ecommerce: {
                currencyCode: this.cartData.currency,
                remove: {
                  products: [
                    {
                      id: payload.item.sku,
                      feedId: payload.item.sku,
                      ...payload.item.product.gtmProductValues,
                    },
                  ],
                },
              },
            };
            pushToDataLayer(trackingPayload);
          }
        }
        this.cartIsUpdating = false;
      },
      async updatePaymentFields(body: object) {
        const { centraPut } = useApiFetch();
        const res = await centraPut<CartType>('/payment-fields', body);
        if (res) {
          this._cartData = res;
        }
      },
      async removeExternalProductsIfSeller() {
        // Remove items flagged as external when the seller flag is set.
        if (!process.client) return false; // only run on client

        const { sellerCookieOptions } = useCookieConfig();
        const isSeller = useCookie('isSeller', sellerCookieOptions);

        const sellerFlag = isSeller.value === true || isSeller.value === 'true';
        if (!sellerFlag) return false;

        const { centraDelete } = useApiFetch();

        // Try multiple sources for items: direct state, getter, or fetch if empty
        let items: any[] = [];
        if (this._cartData?.selection?.items?.length) {
          items = this._cartData.selection.items;
          console.log('got items from 1');
        } else if (this.cartData?.items?.length) {
          items = this.cartData.items;
          console.log('got items from 2');
        } else {
          // attempt to refresh cart from server and re-read
          // eslint-disable-next-line no-console
          console.debug('removeExternalProductsIfSeller: no items in store, fetching cart');
          await this.fetchCart();
          items = this._cartData?.selection?.items || [];
          console.log('got items from 3');
        }
        console.log('Checking for external products to remove, total items: ' + items.length);
        console.log('Cart items: ' + items[0]?.product?.brandName);
        // Collect line ids for items marked as external
        const externalLines = items
          .filter((p) => p.product.brandName !== 'Ullmax');
          //.map((it: any) => it.line)
          //.filter(Boolean) as string[];

        if (!externalLines.length) {
          // nothing to remove
          // eslint-disable-next-line no-console
          console.debug('removeExternalProductsIfSeller: no external lines found');
          return false;
        }

        // eslint-disable-next-line no-console
        console.debug('removeExternalProductsIfSeller: external lines to remove', externalLines);

        for (const line of externalLines) {
          try {
            const res = await centraDelete<CartType>(`/lines/${line.line}`);
            // eslint-disable-next-line no-console
            console.debug('Removed external line:', line.line);
            if (res) {
              this._cartData = res;
            }
          } catch (e) {
            // don't fail the whole operation on a single error; log for debugging
            // eslint-disable-next-line no-console
            console.warn('Failed to remove external line', line, e);
          }
        }
        return true;
      },

      /**
       * Check if round-up product is available without adding it to cart
       * Sets hasRoundUpProductAvailable state
       */
      async checkRoundUpProductAvailability() {
        if (!process.client) return;
        
        const { centraPost } = useApiFetch();
        
        try {
          console.log('[CartStore] Checking round-up product availability...');
          
          // Search for products with the donation attribute
          const searchBody = {
            pr_donation_value: ['1'],
            var_channel: ['Webshop']
          };
          
          const productRes = await centraPost('/products', searchBody);
          
          if (!productRes || !productRes.products || productRes.products.length === 0) {
            console.log('[CartStore] Round-up product not found - trying fallback URI search');
            
            // Fallback: search by URI
            const fallbackRes = await centraPost('/products/round-up');
            
            if (fallbackRes?.product?.items?.[0]?.item) {
              console.log('[CartStore] Round-up product available (via URI)');
              this.hasRoundUpProductAvailable = true;
              return true;
            }
            
            console.log('[CartStore] Round-up product not available');
            this.hasRoundUpProductAvailable = false;
            return false;
          }
          
          const roundUpProduct = productRes.products[0];
          const itemId = roundUpProduct.items?.[0]?.item;
          
          if (!itemId) {
            console.log('[CartStore] Round-up product found but no item ID available');
            this.hasRoundUpProductAvailable = false;
            return false;
          }
          
          console.log('[CartStore] Round-up product available');
          this.hasRoundUpProductAvailable = true;
          return true;
        } catch (error) {
          console.error('[CartStore] Error checking round-up product availability:', error);
          this.hasRoundUpProductAvailable = false;
          return false;
        }
      },

      /**
       * Update or add round-up product to cart
       * Uses the pr_donation_value attribute to find the product across environments
       * @param amount - The amount in base currency units (e.g., SEK)
       */
      async updateRoundUpProduct(amount: number) {
        if (!process.client) return false;
        if (amount <= 0) {
          return this.removeRoundUpProduct();
        }

        // Validate maximum donation amount
        const currency = this.cartData.currency || 'SEK';
        const maxAmount = currency === 'EUR' ? 50 : 500;
        if (amount > maxAmount) {
          console.warn(`[CartStore] Donation amount ${amount} exceeds maximum ${maxAmount}, capping at max`);
          amount = maxAmount;
        }

        const { centraPost, centraPut } = useApiFetch();

        try {
          console.log('[CartStore] updateRoundUpProduct START - amount:', amount);
          console.log('[CartStore] Current cart state:', {
            itemsCount: this.cartData.items?.length,
            items: this.cartData.items?.map((item: any) => ({
              sku: item.sku,
              name: item.product?.name,
              pr_donation_value: item.product?.pr_donation_value
            }))
          });
          
          // First check if round-up product already exists in cart
          console.log('[CartStore] Checking cart items:', this.cartData.items?.length);
          
          const existingRoundUpLine = this.cartData.items.find(
            (item) => item.product?.pr_donation_value === '1'
          );

          console.log('[CartStore] Found existing round-up line:', existingRoundUpLine ? `line ${existingRoundUpLine.line}, current quantity ${existingRoundUpLine.quantity}` : 'none');

          if (existingRoundUpLine) {
            // Product exists - UPDATE the quantity (this SETS it, not adds to it)
            console.log('[CartStore] Found existing round-up, updating line:', existingRoundUpLine.line, 'to quantity:', amount);
            const res = await centraPut<CartType>(
              `/lines/${existingRoundUpLine.line}/quantity/${amount}`
            );
            if (res) {
              this._cartData = res;
              const updatedLine = res.selection?.items?.find((item: any) => item.product?.pr_donation_value === '1');
              console.log('[CartStore] Product quantity updated successfully. New quantity in cart:', updatedLine?.quantity);
              return true;
            }
          } else {
            // Product doesn't exist - ADD it
            console.log('[CartStore] No existing round-up found, adding new');
            
            // Search for products with the donation attribute
            console.log('[CartStore] Searching for donation product...');
            const searchBody = {
              pr_donation_value: ['1'],
              var_channel: ['Webshop']
            };
            console.log('[CartStore] Search request body:', JSON.stringify(searchBody, null, 2));
            const productRes = await centraPost('/products', searchBody);

            console.log('[CartStore] Product search response:', productRes);
            console.log('[CartStore] Product search result:', productRes?.products?.length, 'products found');
            
            if (!productRes || !productRes.products || productRes.products.length === 0) {
              console.error('[CartStore] Round up product not found with pr_donation_value attribute');
              this.hasRoundUpProductAvailable = false;
              return false;
            }

            console.log('[CartStore] Product search response:', productRes);
            console.log('[CartStore] Product search result:', productRes?.products?.length, 'products found');
            
            if (!productRes || !productRes.products || productRes.products.length === 0) {
              console.error('[CartStore] Round up product not found with pr_donation_value attribute in catalog search');
              console.log('[CartStore] Trying alternate search by URI: /products/round-up');
              
              // Fallback: search by URI
              const fallbackRes = await centraPost('/products/round-up');
              console.log('[CartStore] Fallback search result:', fallbackRes);
              console.log('[CartStore] Fallback search product:', fallbackRes?.product);
              
              if (fallbackRes?.product) {
                const roundUpProduct = { product: fallbackRes.product.product, items: fallbackRes.product.items };
                const itemId = roundUpProduct.items?.[0]?.item;
                
                if (itemId) {
                  console.log('[CartStore] Found via URI, adding with itemId:', itemId, 'quantity:', amount);
                  const res = await centraPost<CartType>(
                    `/items/${itemId}/quantity/${amount}`
                  );
                  if (res) {
                    this._cartData = res;
                    console.log('[CartStore] Product added successfully');
                    this.hasRoundUpProductAvailable = true;
                    return true;
                  }
                }
              }
              
              this.hasRoundUpProductAvailable = false;
              return false;
            }

            const roundUpProduct = productRes.products[0];
            const itemId = roundUpProduct.items?.[0]?.item;

            if (!itemId) {
              console.error('No item ID found for round up product');
              this.hasRoundUpProductAvailable = false;
              return false;
            }
            
            this.hasRoundUpProductAvailable = true;

            console.log('[CartStore] Adding new round-up with itemId:', itemId, 'quantity:', amount);
            
            const res = await centraPost<CartType>(
              `/items/${itemId}/quantity/${amount}`
            );
            if (res) {
              this._cartData = res;
              console.log('[CartStore] Product added successfully');
              return true;
            }
          }
        } catch (e) {
          console.error('Failed to update round up product:', e);
          return false;
        }

        return false;
      },

      /**
       * Remove round-up product from cart
       */
      async removeRoundUpProduct() {
        console.log('[CartStore] removeRoundUpProduct START - process.client:', process.client);
        
        if (!process.client) {
          console.log('[CartStore] Returning early - not on client');
          return false;
        }

        const { centraDelete } = useApiFetch();

        try {
          console.log('[CartStore] removeRoundUpProduct called');
          console.log('[CartStore] Current cart items:', this.cartData.items?.length);
          console.log('[CartStore] Cart items:', JSON.stringify(this.cartData.items?.map((item: any) => ({
            line: item.line,
            sku: item.sku,
            quantity: item.quantity,
            pr_donation_value: item.product?.pr_donation_value
          }))));
          
          // Find the round-up product in cart by donation attribute
          const roundUpLine = this.cartData.items.find(
            (item) => item.product?.pr_donation_value === '1'
          );

          console.log('[CartStore] Found round-up line:', roundUpLine ? `line ${roundUpLine.line}, quantity ${roundUpLine.quantity}` : 'none');
          
          if (roundUpLine) {
            console.log('[CartStore] Deleting line:', roundUpLine.line);
            const res = await centraDelete<CartType>(`/lines/${roundUpLine.line}`);
            if (res) {
              this._cartData = res;
              console.log('[CartStore] Round up product removed from cart, new items count:', res.items?.length);
              return true;
            }
          } else {
            console.log('[CartStore] No round-up product found to remove');
          }
        } catch (e) {
          console.error('Failed to remove round up product:', e);
        }

        return false;
      },
    },
    getters: {
      cartData: (state) => new CartData(state._cartData),
      hasRoundUpProduct: (state) => state.hasRoundUpProductAvailable === true,
    },
  }
);
